import { IContractDetailState } from '../../types/contracts/contractTypes';
import { IStaticRateContract } from '../../types/entities/IStaticRateContract';
import {
    convertOccupancyRateStringToJSON,
    orderSeasonsByStartDate,
} from '../../utils/contractUtils/contractUtils';
import { convertDateToUTC } from '../../utils/formatDates';
import { getRandomNumber } from '../../utils/usefulFunctions';

export const adaptStaticRateContractToState = (
    contract: IStaticRateContract
): IContractDetailState => {
    return {
        id: contract.Id,
        enabled: contract.Enabled,
        key: contract.Key || '',
        idStatus:
            contract.StaticRateContractStatus.IdStaticRateContractStatusType,
        status:
            contract.StaticRateContractStatus.StaticRateContractStatusType
                .LocalizedCaption ||
            contract.StaticRateContractStatus.StaticRateContractStatusType
                .Caption ||
            contract.StaticRateContractStatus.StaticRateContractStatusType
                .Name ||
            '',
        statusColor:
            contract.StaticRateContractStatus.StaticRateContractStatusType
                .Color || '',
        name: contract.Name,
        description: contract.Description,
        idAgency: contract.IdAgency,
        agency: contract.Agency.Name,
        idSupplierHotel: contract.IdSupplierHotel,
        supplierHotel: contract.SupplierHotel.Name,
        idRatePlan: contract.IdSupplierHotelRatePlan,
        ratePlan: contract.SupplierHotelRatePlan.Name,
        idRateScopeModel: contract.IdRateScopeModel,
        rateScopeModel:
            contract.RateScopeModel.LocalizedCaption ||
            contract.RateScopeModel.Caption ||
            contract.RateScopeModel.Name ||
            '',
        rateScopeModelKey: contract.RateScopeModel.Key || '',
        activeFrom: contract.ActiveFrom ? new Date(contract.ActiveFrom) : null,
        activeTo: contract.ActiveTo ? new Date(contract.ActiveTo) : null,
        idCurrency: contract.IdCurrency,
        currency: contract.Currency.Name,
        seasons: orderSeasonsByStartDate(
            contract.Seasons.map(season => ({
                id: season.Id,
                enabled: season.Enabled,
                start: season.FromDate ? new Date(season.FromDate) : null,
                end: season.ToDate ? new Date(season.ToDate) : null,
                name: season.Name,
            }))
        ), // ascendent orden by start date,
        rooms: contract.RoomTypes.map(room => ({
            id: room.Id,
            enabled: room.Enabled,
            name: room.Name,
            idSupplierHotelRoomType: room.IdSupplierHotelRoomType,
            supplierHotelRoomType: room.SupplierHotelRoomType.Name,
            maxOccupancy: room.SupplierHotelRoomType.MaxOccupancy,
            minAdults: room.SupplierHotelRoomType.MinAdults,
            maxAdults: room.SupplierHotelRoomType.MaxAdults,
            maxChildren: room.SupplierHotelRoomType.MaxChild,
            minChildren: room.SupplierHotelRoomType.MinChildren,
            standardOccupancy: room.SupplierHotelRoomType.StandardOccupancy,
            occupancy: room.SupplierHotelRoomType.Occupancy,
            idRateScopeModel: room.SupplierHotelRoomType.IdRateScopeModel,
            rateScopeModel:
                room.SupplierHotelRoomType.RateScopeModel.LocalizedCaption ||
                room.SupplierHotelRoomType.RateScopeModel.Caption ||
                room.SupplierHotelRoomType.RateScopeModel.Name ||
                '',
            rateScopeModelKey:
                room.SupplierHotelRoomType.RateScopeModel.Key || '',
            adultsOnly:
                contract.SupplierHotel.AdultsOnly ||
                room.SupplierHotelRoomType.AdultOnly,
        })),
        mealPlans: contract.MealPlans.map(mealPlan => {
            return {
                id: mealPlan.Id,
                enabled: mealPlan.Enabled,
                name: mealPlan.Name,
                idMealPlan: mealPlan.IdSupplierHotelMealPlan,
                mealPlan: mealPlan.SupplierHotelMealPlan.Name,
                isRoomOnly: mealPlan.SupplierHotelMealPlan.IsRoomOnly,
                isBedAndBreakfast:
                    mealPlan.SupplierHotelMealPlan.IsBedAndBreakfast,
                type: mealPlan.SupplierHotelMealPlan.MealPlanType.Name,
            };
        }),
        policies: contract.Policies.map(policy => ({
            id: policy.Id,
            content: policy.Content,
            name: policy.Name,
        })),
        rateDates: contract.RateDates.map(rate => {
            return {
                id: rate.Id,
                idRoom: rate.IdStaticRateRoomType,
                room: rate.StaticRateRoomType.Name,
                occupancy:
                    rate.StaticRateRoomType.SupplierHotelRoomType.Occupancy,
                maxOccupancy:
                    rate.StaticRateRoomType.SupplierHotelRoomType.MaxOccupancy,
                standardOccupancy:
                    rate.StaticRateRoomType.SupplierHotelRoomType
                        .StandardOccupancy,
                minAdults:
                    rate.StaticRateRoomType.SupplierHotelRoomType.MinAdults,
                maxAdults:
                    rate.StaticRateRoomType.SupplierHotelRoomType.MaxAdults,
                minChildren:
                    rate.StaticRateRoomType.SupplierHotelRoomType.MinChildren,
                maxChildren:
                    rate.StaticRateRoomType.SupplierHotelRoomType.MaxChild,
                idRoomScopeModel:
                    rate.StaticRateRoomType.SupplierHotelRoomType
                        .IdRateScopeModel,
                roomScopeModel:
                    rate.StaticRateRoomType.SupplierHotelRoomType.RateScopeModel
                        .LocalizedCaption ||
                    rate.StaticRateRoomType.SupplierHotelRoomType.RateScopeModel
                        .Caption ||
                    rate.StaticRateRoomType.SupplierHotelRoomType.RateScopeModel
                        .Name ||
                    '',
                roomScopeModelKey:
                    rate.StaticRateRoomType.SupplierHotelRoomType.RateScopeModel
                        .Key || '',
                adultsOnly:
                    contract.SupplierHotel.AdultsOnly ||
                    rate.StaticRateRoomType.SupplierHotelRoomType.AdultOnly,
                idRateScopeModel: contract.IdRateScopeModel,
                rateScopeModel:
                    contract.RateScopeModel.LocalizedCaption ||
                    contract.RateScopeModel.Caption ||
                    contract.RateScopeModel.Name ||
                    '',
                rateScopeModelKey: contract.RateScopeModel.Key || '',
                idSeason: rate.IdStaticRateSeason,
                season: rate.StaticRateSeason.Name,
                idMealPlan: rate.IdStaticRateMealPlan,
                mealPlan: rate.StaticRateMealPlan.Name,
                baseRate: rate.BaseRate,
                currentInventory: rate.CurrentInventory,
                initialInventory: rate.InitialInventory,
                maxBookingAdvancedOffset: rate.MaxBookingAdvancedOffset,
                maxStayAtArrival: rate.MaxStayAtArrival,
                minBookingAdvancedOffset: rate.MinBookingAdvancedOffset,
                minStayAtArrival: rate.MinStayAtArrival,
                occupancyRates: convertOccupancyRateStringToJSON(
                    rate.OccupancyRates
                ),
            };
        }),
        ageSegments: contract.SupplierHotel.AdultsOnly
            ? []
            : contract.SupplierHotel.ChildrenAgeSegments.map(segment => {
                  return {
                      id: parseInt(segment.Id),
                      name: segment.Caption,
                      age: segment.Age,
                  };
              }),
    };
};

export const adaptStaticRateContractMainInfoToDDBB = (
    contract: Partial<IContractDetailState>
) => {
    return {
        Id: contract.id ? contract.id : `-${getRandomNumber()}`,
        Enabled: contract.enabled,
        Key: contract.key,
        Name: contract.name,
        Description: contract.description,
        IdAgency: contract.idAgency,
        IdSupplierHotel: contract.idSupplierHotel,
        IdSupplierHotelRatePlan: contract.idRatePlan,
        IdRateScopeModel: contract.idRateScopeModel,
        ActiveFrom: convertDateToUTC(contract.activeFrom),
        ActiveTo: convertDateToUTC(contract.activeTo),
        IdCurrency: contract.idCurrency,
        StaticRateContractStatus: {
            IdStaticRateContractStatusType: contract.idStatus,
        },
    };
};

export const adaptStaticRateContractToDDBB = (
    contract: Partial<IContractDetailState>
) => {
    const mainInfo = adaptStaticRateContractMainInfoToDDBB(contract);
    return {
        ...mainInfo,
        Seasons:
            contract.seasons?.map(season => ({
                Id: season.id ? season.id : `-${getRandomNumber()}`,
                Name: season.name,
                FromDate: convertDateToUTC(season.start),
                ToDate: convertDateToUTC(season.end),
                Enabled: season.enabled,
                __isAdded: season.__isAdded,
                __isChanged: season.__isChanged,
                Deleted: season.Deleted,
            })) || [],
        RoomTypes:
            contract.rooms?.map(room => ({
                Id: room.id ? room.id : `-${getRandomNumber()}`,
                Name: room.name,
                IdSupplierHotelRoomType: room.idSupplierHotelRoomType,
                Enabled: room.enabled,
                __isAdded: room.__isAdded,
                __isChanged: room.__isChanged,
                Deleted: room.Deleted,
            })) || [],
        MealPlans:
            contract.mealPlans?.map(mealPlan => ({
                Id: mealPlan.id ? mealPlan.id : `-${getRandomNumber()}`,
                Name: mealPlan.name,
                IdSupplierHotelMealPlan: mealPlan.idMealPlan,
                Enabled: mealPlan.enabled,
                __isAdded: mealPlan.__isAdded,
                __isChanged: mealPlan.__isChanged,
                Deleted: mealPlan.Deleted,
            })) || [],
        Policies:
            contract.policies?.map(policy => ({
                Id: policy.id ? policy.id : `-${getRandomNumber()}`,
                Name: policy.name,
                Content: policy.content,
                __isAdded: policy.__isAdded,
                __isChanged: policy.__isChanged,
                Deleted: policy.Deleted,
            })) || [],
        RateDates:
            contract.rateDates?.map(rate => ({
                Id: rate.id ? rate.id : `-${getRandomNumber()}`,
                IdStaticRateRoomType: rate.idRoom,
                IdStaticRateSeason: rate.idSeason,
                IdStaticRateMealPlan: rate.idMealPlan,
                OccupancyRates: JSON.stringify(rate.occupancyRates),
                BaseRate: rate.baseRate,
                CurrentInventory: rate.currentInventory,

                InitialInventory: rate.initialInventory,
                MaxBookingAdvancedOffset: rate.maxBookingAdvancedOffset,
                MaxStayAtArrival: rate.maxStayAtArrival,
                MinBookingAdvancedOffset: rate.minBookingAdvancedOffset,
                MinStayAtArrival: rate.minStayAtArrival,
                __isAdded: rate.__isAdded,
                __isChanged: rate.__isChanged,
                Deleted: rate.Deleted,
            })) || [],
    };
};
